/**
 * IDENTITÉ CRYPTOGRAPHIQUE DU CLIENT DE RECETTE.
 *
 * L'application iPad rangera sa clé privée dans le Keychain, adossée au Secure
 * Enclave quand le matériel le permet. Un navigateur n'a pas d'équivalent —
 * mais il a mieux qu'un pis-aller : **une clé WebCrypto NON EXPORTABLE**.
 *
 * Générée avec `extractable: false`, la clé privée ne peut plus jamais sortir
 * du navigateur : ni `exportKey`, ni lecture depuis la console, ni copie dans
 * le stockage local. Elle est conservée dans IndexedDB sous forme d'objet
 * `CryptoKey` — c'est-à-dire une POIGNÉE vers un secret que le moteur garde,
 * pas le secret lui-même.
 *
 * C'est pour cela que ce client peut utiliser exactement le même contrat que
 * l'application native, sans qu'on ait à maintenir une route moins sûre à côté.
 */

const BASE = 'gestion.pointeuse'
const STORE = 'device'
const CLE = 'signing-key'

interface Paire { publicKey: CryptoKey; privateKey: CryptoKey }

function ouvrir(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(BASE, 2)
    r.onupgradeneeded = () => {
      const db = r.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
      // Le magasin de la file hors ligne du lot précédent a disparu avec elle.
    }
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error ?? new Error('IndexedDB indisponible'))
  })
}

async function lire<T>(cle: string): Promise<T | null> {
  const db = await ouvrir()
  return new Promise((resolve) => {
    const t = db.transaction(STORE, 'readonly').objectStore(STORE).get(cle)
    t.onsuccess = () => resolve((t.result as T | undefined) ?? null)
    t.onerror = () => resolve(null)
  })
}

async function ecrire(cle: string, valeur: unknown): Promise<void> {
  const db = await ouvrir()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(valeur, cle)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('Écriture impossible'))
  })
}

export async function effacerCleAppareil(): Promise<void> {
  const db = await ouvrir()
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).delete(CLE)
    tx.oncomplete = () => resolve()
    tx.onerror = () => resolve()
  })
}

/**
 * La paire de l'appareil — générée une seule fois, puis retrouvée.
 *
 * `extractable: false` est le point important : sans lui, la clé privée
 * pourrait être exportée et copiée sur une autre machine, ce qui reviendrait
 * exactement au jeton volé qu'on vient de rendre inutilisable.
 */
export async function paireAppareil(): Promise<Paire> {
  const existante = await lire<Paire>(CLE)
  if (existante?.privateKey) return existante
  const paire = (await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify'],
  )) as CryptoKeyPair
  const valeur: Paire = { publicKey: paire.publicKey, privateKey: paire.privateKey }
  await ecrire(CLE, valeur)
  return valeur
}

const b64 = (b: ArrayBuffer): string => btoa(String.fromCharCode(...new Uint8Array(b)))

/** Clé publique au format X9.63 non compressé — `raw` de WebCrypto, `x963Representation` de CryptoKit. */
export async function clePubliqueBase64(paire: Paire): Promise<string> {
  return b64(await crypto.subtle.exportKey('raw', paire.publicKey))
}

export async function sha256Hex(texte: string): Promise<string> {
  const h = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(texte))
  return [...new Uint8Array(h)].map((x) => x.toString(16).padStart(2, '0')).join('')
}

/**
 * La chaîne canonique — MÊME construction que le serveur, neuf lignes.
 *
 * Toute divergence, fût-ce d'un espace, rendrait chaque signature invalide.
 * C'est pourquoi le contrat en publie un vecteur de test : une implémentation
 * qui le reproduit octet pour octet est forcément compatible.
 */
export async function chaineCanonique(p: {
  method: string; path: string; query?: string; body?: string
  stationId: string; keyId: string; timestamp: string; nonce: string
}): Promise<string> {
  const query = normaliserQuery(p.query ?? '')
  return [
    'gcs1', p.method.toUpperCase(), p.path, query, await sha256Hex(p.body ?? ''),
    p.stationId, p.keyId, p.timestamp, p.nonce,
  ].join('\n')
}

export function normaliserQuery(q: string): string {
  const s = q.replace(/^\?/, '')
  if (!s) return ''
  const paires: Array<[string, string]> = []
  for (const m of s.split('&')) {
    if (!m) continue
    const i = m.indexOf('=')
    paires.push([decodeURIComponent(i === -1 ? m : m.slice(0, i)), i === -1 ? '' : decodeURIComponent(m.slice(i + 1))])
  }
  paires.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0))
  return paires.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')
}

/** Signature ECDSA P-256 / SHA-256. WebCrypto rend déjà du r‖s brut : rien à convertir. */
export async function signer(paire: Paire, canonique: string): Promise<string> {
  const sig = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' }, paire.privateKey, new TextEncoder().encode(canonique),
  )
  return b64(sig)
}
